import { useEffect } from 'react'
import DocLayout from '../../components/docs/DocLayout'
import Icon from '../../components/docs/Icon'
import { displayFonts, monoFont, sansFonts, type FontOption } from '@/content/fonts'
import { tokens } from '@/content/system'
import { usePrefs } from '@/state/prefs'
import { loadFont } from '@/state/fontLoaders'
import Cased from '@/components/ui/Cased'

const scaleUse: Record<string, string> = {
  hero: 'Design for America.',
  display: 'Every agency, ready.',
  h1: 'Apply for a passport',
  h2: 'Check your eligibility',
  h3: 'What you need to apply',
  lead: 'Renew online in about ten minutes with a recent photo.',
  body: 'You can renew by mail if your most recent passport was issued when you were 16 or older.',
  small: 'Processing times are updated weekly.',
  micro: 'An official website of the United States government',
}

function FontCard({
  font,
  kind,
  active,
  onApply,
}: {
  font: FontOption
  kind: 'display' | 'sans'
  active: boolean
  onApply: () => void
}) {
  return (
    <article className={`fnd-font${active ? ' is-active' : ''}`}>
      <p className={`fnd-font__sample fnd-font__sample--${kind}`} style={{ fontFamily: font.stack }} aria-hidden="true">
        {kind === 'display' ? 'Design for America.' : 'Whatever you need from government, start here.'}
      </p>
      <div className="fnd-font__meta">
        <h3 className="mds-card__title"><Cased>{font.name}</Cased></h3>
        <p className="mds-muted">{font.note}</p>
        <p className="fnd-font__credit">
          {font.designer}. {font.license ?? 'SIL Open Font License'}. <code>{font.package}</code>
        </p>
        <div className="mds-cluster">
          <button type="button" className="mds-button mds-button--sm mds-button--secondary" aria-pressed={active} onClick={onApply}>
            {active ? (
              <>
                <Icon name="check" size={14} /> In use
              </>
            ) : (
              'Use this typeface'
            )}
            <span className="mds-visually-hidden"> {font.name}</span>
          </button>
          <a className="mds-link fnd-font__specimen" href={font.specimen}>
            Specimen<span className="mds-visually-hidden"> of {font.name}</span>
          </a>
        </div>
      </div>
    </article>
  )
}

export default function TypographyPage() {
  const { prefs, set } = usePrefs()
  const scale = tokens.filter((token) => token.css.startsWith('--mds-text-'))

  // Load every alternate so the specimens render in their own faces.
  useEffect(() => {
    for (const font of [...displayFonts, ...sansFonts]) loadFont(font.id)
  }, [])

  return (
    <DocLayout section={{ label: 'Foundations', to: '/foundations' }}
      eyebrow="Foundations"
      title="Typography"
      intro={
        <p>
          A display serif gives headlines a voice; a precise sans carries everything a person reads and does. Every
          typeface is open-license from the Google Fonts catalog, self-hosted through Fontsource, and switches by token.
        </p>
      }
      wide
    >
      <section className="doc-section" aria-labelledby="type-display">
        <h2 id="type-display"><Cased>Display serif</Cased></h2>
        <p>
          Instrument Serif is the default: condensed, high-contrast and at ease at very large sizes. Use it for heroes,
          page titles and section headings, at regular weight.
        </p>
        <div className="fnd-fonts">
          {displayFonts.map((font) => (
            <FontCard
              key={font.id}
              font={font}
              kind="display"
              active={prefs.fontDisplay === font.id}
              onApply={() => set('fontDisplay', font.id)}
            />
          ))}
        </div>
      </section>

      <section className="doc-section" aria-labelledby="type-sans">
        <h2 id="type-sans"><Cased>Body and interface sans</Cased></h2>
        <p>
          Inter is the default: a neutral grotesque drawn for screens, with tabular figures for tables and dashboards.
          Use it for body text, labels, buttons and any heading set in the sans.
        </p>
        <div className="fnd-fonts">
          {sansFonts.map((font) => (
            <FontCard
              key={font.id}
              font={font}
              kind="sans"
              active={prefs.fontSans === font.id}
              onApply={() => set('fontSans', font.id)}
            />
          ))}
        </div>
      </section>

      <section className="doc-section" aria-labelledby="type-mono">
        <h2 id="type-mono"><Cased>Mono</Cased></h2>
        <p>
          {monoFont.name} sets code, token names and technical labels such as eyebrows. {monoFont.designer}. SIL Open
          Font License.
        </p>
        <pre className="mds-code" tabIndex={0}>
          <code>{`--mds-font-display: ${displayFonts[0].stack};\n--mds-font-sans: ${sansFonts[0].stack};\n--mds-font-mono: ${monoFont.stack};`}</code>
        </pre>
      </section>

      <section className="doc-section" aria-labelledby="type-scale">
        <h2 id="type-scale"><Cased>Scale</Cased></h2>
        <p>
          Sizes are fluid: each step grows with the viewport between a floor and a ceiling, so one token serves a phone
          and a wide display.
        </p>
        <ol className="fnd-scale">
          {scale.map((token) => {
            const key = token.css.replace('--mds-text-', '')
            const serif = ['hero', 'display', 'h1', 'h2'].includes(key)
            return (
              <li key={token.css} className="fnd-scale__row">
                <div className="fnd-scale__meta">
                  <code>{token.css}</code>
                  <span className="mds-muted">{token.role}</span>
                </div>
                <p
                  className={`fnd-scale__sample${serif ? ' fnd-scale__sample--serif' : ''}`}
                  style={{ fontSize: `var(${token.css})` }}
                >
                  {scaleUse[key] ?? token.name}
                </p>
              </li>
            )
          })}
        </ol>
      </section>

      <section className="doc-section" aria-labelledby="type-guidance">
        <h2 id="type-guidance"><Cased>Guidance</Cased></h2>
        <ul>
          <li>One hero line per page, in the display serif.</li>
          <li>Page titles and section headings in the serif; subsections, labels and controls in the sans.</li>
          <li>Keep reading text at 16px or larger, at a line length near 70 characters.</li>
          <li>Set the serif at regular weight; its contrast does the work bold would.</li>
          <li>Write in sentence case. Reserve uppercase for short mono eyebrows.</li>
          <li>Load alternates only when a mission uses them; the defaults ship with every page.</li>
        </ul>
      </section>
    </DocLayout>
  )
}
